import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { BrandgenPublicationList } from "../../../types/campaignTypes";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import {
  getCustomDomainLists,
  getPublicDomainLists,
} from "../../CampaignCreationCustomSlides/PublicationListSelectionSlide/publicationListApi";
import { setCustomDomainLists, setPublicDomainLists } from "../../../redux/reducers/campaignToCreate";
import { useGlobalSelectedAdvertiser } from "../../../hooks/useGlobalSelectedAdvertiser";

/**
 * In-flight custom-list requests keyed by advertiser. Module scoped so that several
 * consumers mounted at once (e.g. the sets table plus the browse inside its modal)
 * share one request instead of each firing their own.
 */
const inFlightCustomFetches = new Map<string, Promise<BrandgenPublicationList[]>>();

/** Loads the public + custom publication lists into Redux and exposes them. */
export const usePublicationLists = () => {
  const dispatch = useDispatch();
  const { brandgenAdvertiserId } = useGlobalSelectedAdvertiser();
  const selectedAdvertiserId = brandgenAdvertiserId ?? "";

  const publicLists = useSelector((state: ReduxApplicationState) => state.campaignToCreate.publicDomainLists);
  const customLists = useSelector((state: ReduxApplicationState) => state.campaignToCreate.customDomainLists);

  const [isLoadingPublic, setIsLoadingPublic] = useState(false);
  const [isLoadingCustom, setIsLoadingCustom] = useState(false);

  useEffect(() => {
    if (publicLists.length > 0) return;
    const load = async () => {
      try {
        setIsLoadingPublic(true);
        const lists = await getPublicDomainLists();
        dispatch(setPublicDomainLists(lists));
      } catch (error) {
        console.error("fetchPublicDomainLists:", error);
      } finally {
        setIsLoadingPublic(false);
      }
    };
    load();
  }, [dispatch, publicLists.length]);

  const fetchCustom = useCallback(
    async (advertiserId: string) => {
      if (!advertiserId) return;
      setIsLoadingCustom(true);
      try {
        const pending = inFlightCustomFetches.get(advertiserId) ?? getCustomDomainLists(advertiserId);
        inFlightCustomFetches.set(advertiserId, pending);
        const lists = await pending;
        dispatch(setCustomDomainLists(lists ?? []));
      } catch (error) {
        console.error("fetchCustomDomainLists:", error);
        dispatch(setCustomDomainLists([]));
      } finally {
        inFlightCustomFetches.delete(advertiserId);
        setIsLoadingCustom(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (!selectedAdvertiserId) return;
    fetchCustom(selectedAdvertiserId);
  }, [fetchCustom, selectedAdvertiserId]);

  const allLists = useMemo(() => [...publicLists, ...customLists], [publicLists, customLists]);

  return {
    selectedAdvertiserId,
    publicLists,
    customLists,
    allLists,
    isLoadingPublic,
    isLoadingCustom,
    refetchCustom: () => fetchCustom(selectedAdvertiserId),
  };
};
